import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Loader2, Search, Wifi } from 'lucide-react';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { parseRouterConfigSettings, RouterConfig } from '@/shared/types/routerTypes';
import { RouterData } from '@/shared/types/userTerminalTypes';
import { useAssignRouterConfig, usePaginatedRouterConfigs } from '@/domains/routers/services/account-routers';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';

interface ApplyRouterConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  router: RouterData | null;
  refetchTerminal: () => void;
}

const ApplyRouterConfigDialog = ({ open, onOpenChange, router, refetchTerminal }: ApplyRouterConfigDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [configId, setConfigId] = useState<string>(router?.configId ?? '');

  const {
    data: routerConfigsInfiniteData,
    isLoading: isLoadingRouterConfigs,
    isFetchingNextPage: isFetchingNextRouterConfigs,
    fetchNextPage: fetchNextRouterConfigs,
    hasNextPage: hasNextRouterConfigs,
  } = usePaginatedRouterConfigs();

  const mergedRouterConfigs = useMemo(() => {
    const pages = routerConfigsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: RouterConfig[] = [];
    for (const page of pages) {
      for (const config of page.results ?? []) {
        if (!seen.has(config.configId)) {
          seen.add(config.configId);
          merged.push(config);
        }
      }
    }
    return merged;
  }, [routerConfigsInfiniteData?.pages]);

  const totalRouterConfigCount = routerConfigsInfiniteData?.pages[0]?.totalCount ?? 0;

  const filteredRouterConfigs = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) return mergedRouterConfigs;
    return mergedRouterConfigs.filter((config) => {
      const settings = parseRouterConfigSettings(config.routerConfigJson);
      const ssids = settings?.networks?.flatMap((n) => n.basicServiceSets?.map((bss) => bss.ssid) ?? []) ?? [];
      return [config.nickname, config.configId, ...ssids].some((value) => value?.toLowerCase().includes(q));
    });
  }, [mergedRouterConfigs, searchTerm]);

  const applyConfig = useAssignRouterConfig();

  useEffect(() => {
    if (open) {
      setConfigId(router?.configId ?? '');
    } else {
      setConfigId('');
      setSearchTerm('');
    }
  }, [open, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!router) return;
    if (!configId) {
      showError(ERROR.ROUTER.SELECT_CONFIG);
      return;
    }

    try {
      await applyConfig.mutateAsync({ routerIds: [router.routerId], configId });
      showSuccess(SUCCESS.ROUTER.CONFIG_APPLIED);
      onOpenChange(false);
      refetchTerminal();
    } catch (error) {
      showErrorWithApiError(error, ERROR.ROUTER.APPLY_CONFIG);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="sm:max-w-[600px]"
      >
        <DialogHeader>
          <DialogTitle>Apply Wi-Fi Config</DialogTitle>
          <DialogDescription>
            Push a saved Starlink Wi-Fi / bypass profile to {router?.nickname || router?.routerId}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2 flex flex-col gap-2">
              <div className="relative">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search configs by name, ID, or SSID..."
                />
              </div>

              <p className="text-sm font-medium">Select Wi-Fi Config</p>
              <ScrollArea className="h-[300px] w-full rounded-md border">
                <div className="p-4 space-y-3">
                  {isLoadingRouterConfigs && mergedRouterConfigs.length === 0 ? (
                    <div className="flex justify-center items-center min-h-[200px]">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : filteredRouterConfigs.length > 0 ? (
                    <RadioGroup
                      value={configId}
                      onValueChange={setConfigId}
                      className="space-y-2"
                    >
                      {filteredRouterConfigs.map((config) => {
                        const settings = parseRouterConfigSettings(config.routerConfigJson);
                        const ssids = settings?.networks?.flatMap((n) => n.basicServiceSets?.map((bss) => bss.ssid) ?? []) ?? [];
                        const ssidSummary = ssids.filter(Boolean).join(' · ') || 'No SSIDs configured';

                        return (
                          <div key={config.configId} className="flex items-center space-x-2">
                            <RadioGroupItem
                              value={config.configId}
                              id={`config-${config.configId}`}
                            />
                            <Label
                              htmlFor={`config-${config.configId}`}
                              className="text-sm font-normal flex-1"
                            >
                              <div className="flex flex-col">
                                <span>{config.nickname || 'Unnamed config'}{settings?.bypassMode ? ' (Bypass)' : ''}</span>
                                <span className="text-xs text-muted-foreground">{ssidSummary}</span>
                                <span className="text-xs text-muted-foreground">{config.configId}</span>
                              </div>
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  ) : searchTerm ? (
                    <EmptyView
                      icon={Search}
                      size="small"
                      title="No Wi-Fi Configs Found"
                      description={`No configs match "${searchTerm}". Try adjusting your search.`}
                    />
                  ) : hasNextRouterConfigs ? (
                    <EmptyView
                      icon={Wifi}
                      size="small"
                      title="No Wi-Fi Configs Yet"
                      description="No configs on the pages loaded so far. Load more to search further."
                    />
                  ) : (
                    <EmptyView
                      icon={Wifi}
                      size="small"
                      title="No Wi-Fi Configs Available"
                      description="Create a Wi-Fi config first, then apply it to this router."
                    />
                  )}
                  {(hasNextRouterConfigs || (totalRouterConfigCount > 0 && mergedRouterConfigs.length > 0)) && (
                    <div className="glass-panel flex flex-col gap-2 border-t p-2 rounded-lg">
                      {totalRouterConfigCount > 0 && (
                        <p className="text-xs">
                          Loaded {mergedRouterConfigs.length} of {totalRouterConfigCount} configs
                          {filteredRouterConfigs.length !== mergedRouterConfigs.length && (
                            <span> · {filteredRouterConfigs.length} match search</span>
                          )}
                          {hasNextRouterConfigs && (
                            <span> · {totalRouterConfigCount - mergedRouterConfigs.length} more configs available to load</span>
                          )}
                        </p>
                      )}
                      {hasNextRouterConfigs ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          disabled={isFetchingNextRouterConfigs}
                          onClick={() => void fetchNextRouterConfigs()}
                        >
                          {isFetchingNextRouterConfigs ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading…
                            </>
                          ) : (
                            'Load more'
                          )}
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={applyConfig.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={applyConfig.isPending || !configId}
              className="gap-2"
            >
              {applyConfig.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                'Apply Config'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyRouterConfigDialog;
